import { Component, Input, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { ProspectSummary } from 'src/app/domain/prospect.model';
import { ProspectsService } from 'src/app/shared/services/prospects.service';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import { Chart } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-prospect-summary-customers',
  templateUrl: './prospect-summary-customers.component.html',
  styleUrls: ['./prospect-summary-customers.component.scss']
})
export class ProspectSummaryCustomersComponent implements OnInit, AfterViewInit {
  @ViewChild('dtProspectSummary') table!: Table;
  @ViewChild('prospectSummaryChart') prospectSummaryChart!: UIChart;

  // ✅ Input: user ID for potential future use (currently not needed)
  @Input() userId: number = 0;

  // Data
  prospectSummary: ProspectSummary[] = [];
  filteredProspectSummary: ProspectSummary[] = [];

  // Chart
  prospectSummaryChartData: any = { labels: [], datasets: [] };
  prospectSummaryChartOptions: any;
  hiddenDatasets: boolean[] = [];

  // Filters & Controls
  customerNameOptions: any[] = [];
  statusOptions: any[] = [];
  selectedProspectSummaryDataType: string = 'prospectCount';
  selectedProspectSummaryGroupBy: string = 'customerName';
  selectedCustomerScope: string = 'all';
  selectedCustomerCount: number = 10;

sectorNameOptions: any[] = [];
selectedSectors: string[] = []; // for table filter

  isSorted: boolean = false;
  currentYear = new Date().getFullYear();
  yearOptions: { label: string; value: number }[] = [];
  selectedYears: number[] = [];

  // Dropdown options
  prospectSummaryDataTypes = [
    { label: 'Prospect Count', value: 'prospectCount' },
    { label: 'Total Service Value', value: 'totalServiceValue' },
    { label: 'Total Activities', value: 'totalActivities' }
  ];

  prospectSummaryGroupByOptions = [
    { label: 'Customer Name', value: 'customerName' },
    { label: 'Status', value: 'status' }
  ];

  customerScopeOptions = [
    { label: 'Top', value: 'top' },
    { label: 'All', value: 'all' },
    { label: 'Bottom', value: 'bottom' }
  ];

  constructor(private prospectsService: ProspectsService) {
    Chart.register(this.customLabelsPlugin);
  }

  ngOnInit() {
    this.generateYearOptions();
    this.selectedYears = this.yearOptions.map(opt => opt.value);
    this.initializeChart();
    this.loadData();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.prospectSummaryChart?.chart) {
        this.prospectSummaryChart.chart.update();
      }
    }, 100);
  }

  private generateYearOptions() {
    this.yearOptions = [];
    for (let y = 2022; y <= this.currentYear; y++) {
      this.yearOptions.push({ label: y.toString(), value: y });
    }
  }

  loadData() {
    this.prospectsService.getProspectSummaryByCustomers()
      .toPromise()
      .then(response => {
        this.prospectSummary = response;
        this.filteredProspectSummary = response;
        this.buildFilterOptions();
        this.updateChart();
      })
      .catch(error => console.error('Error loading data:', error));
  }

  loadByYears(years: number[]) {
    this.prospectsService.getProspectSummaryByCustomers(years)
      .toPromise()
      .then(response => {
        this.prospectSummary = response;
        this.filteredProspectSummary = response;
        this.updateChart();
      })
      .catch(error => {
        console.error('Error loading by years:', error);
        this.filteredProspectSummary = [];
        this.updateChart();
      });
  }

   

  private buildFilterOptions() {
  const uniqueCustomerNames = [...new Set(this.prospectSummary.map(item => item.customerName))];
  this.customerNameOptions = uniqueCustomerNames.map(name => ({ label: name, value: name }));

  const uniqueStatuses = [...new Set(this.prospectSummary.map(item => item.status))];
  this.statusOptions = uniqueStatuses.map(status => ({ label: status, value: status }));

  // ✅ NEW: Extract unique sectors
  const uniqueSectors = [...new Set(this.prospectSummary.map(item => item.sectorName).filter(s => s))]; // exclude null/empty
  this.sectorNameOptions = uniqueSectors.map(name => ({ label: name, value: name }));
}

  initializeChart() {
    this.prospectSummaryChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      scales: {
        x: {
          stacked: true,
          ticks: {
            autoSkip: false,
            maxRotation: 90,
            minRotation: 90,
            padding: 4,
            font: { size: 10 }
          }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grace: '5%',
          ticks: { precision: 0 }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const datasetLabel = tooltipItem.dataset.label;
              const value = tooltipItem.raw;
              if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
                return `${datasetLabel}: $${value.toLocaleString()}`;
              }
              return `${datasetLabel}: ${value}`;
            },
            footer: (tooltipItems) => {
              const dataIndex = tooltipItems[0].dataIndex;
              const allDatasets = tooltipItems[0].chart.data.datasets;
              let total = 0;
              for (let i = 0; i < allDatasets.length; i++) {
                total += allDatasets[i].data[dataIndex] || 0;
              }
              if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
                return `Total: $${total.toLocaleString()}`;
              }
              return `Total: ${total}`;
            }
          },
          usePointStyle: true,
          boxWidth: 6
        }
      }
    };
  }

  updateChart() {
    const dataToDisplay = [...this.filteredProspectSummary];

    // Group first
    let groupedData;
    if (this.selectedProspectSummaryGroupBy === 'customerName') {
      groupedData = this.groupDataByCustomer(dataToDisplay);
    } else {
      groupedData = this.groupDataByStatus(dataToDisplay);
    }

    // Calculate total per group (for sorting)
    const groupTotals: { [key: string]: number } = {};
    Object.keys(groupedData).forEach(group => {
      let total = 0;
      Object.values(groupedData[group]).forEach((value: number) => {
        total += value;
      });
      groupTotals[group] = total;
    });

    // Sort group names by total
    let sortedGroupNames = Object.keys(groupTotals);
    if (this.selectedCustomerScope === 'top' || (this.isSorted && this.selectedCustomerScope === 'all')) {
      sortedGroupNames.sort((a, b) => groupTotals[b] - groupTotals[a]);
    } else if (this.selectedCustomerScope === 'bottom') {
      sortedGroupNames.sort((a, b) => groupTotals[a] - groupTotals[b]);
    }

    // Apply count limit ONLY when grouping by customer
    if (this.selectedProspectSummaryGroupBy === 'customerName' &&
        (this.selectedCustomerScope === 'top' || this.selectedCustomerScope === 'bottom')) {
      sortedGroupNames = sortedGroupNames.slice(0, this.selectedCustomerCount);
    }

    // Rebuild groupedData in sorted order
    const sortedGroupedData: { [key: string]: any } = {};
    sortedGroupNames.forEach(name => {
      sortedGroupedData[name] = groupedData[name];
    });

    // Generate chart data
    const labels = sortedGroupNames;
    const datasets = this.createDatasets(sortedGroupedData);

    this.prospectSummaryChartData = { labels, datasets };
    this.hiddenDatasets = new Array(datasets.length).fill(false);

    // Y-axis formatting
    if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
      this.prospectSummaryChartOptions.scales.y.ticks.callback = (value: number) =>
        value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`;
    } else {
      this.prospectSummaryChartOptions.scales.y.ticks = { precision: 0 };
    }

    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.update();
    }
  }
  
  groupDataByCustomer(data: ProspectSummary[]) {
    return data.reduce((acc, item) => {
      if (!acc[item.customerName]) acc[item.customerName] = {};
      acc[item.customerName][item.status] = item[this.selectedProspectSummaryDataType];
      return acc;
    }, {} as Record<string, Record<string, number>>);
  }

  groupDataByStatus(data: ProspectSummary[]) {
    return data.reduce((acc, item) => {
      if (!acc[item.status]) acc[item.status] = {};
      acc[item.status][item.customerName] = item[this.selectedProspectSummaryDataType];
      return acc;
    }, {} as Record<string, Record<string, number>>);
  }

  createDatasets(groupedData: Record<string, Record<string, number>>) {
    const colorPalette = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
      '#808080', '#008000', '#8B4513', '#00CED1', '#FFA500', '#FF00FF'
    ];
    const groups = new Set<string>();
    Object.values(groupedData).forEach(data => {
      Object.keys(data).forEach(group => groups.add(group));
    });
    const groupArray = Array.from(groups);
    return groupArray.map((group, index) => {
      const data = Object.keys(groupedData).map(key => groupedData[key][group] || 0);
      const color = colorPalette[index % colorPalette.length];
      return {
        label: group,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
  }

  // Event Handlers
  onTableFilter(event: any) {
  // event.filteredValue is the filtered rows
  this.filteredProspectSummary = event.filteredValue || [];

  // ✅ Capture selected sectors from filters
  if (event.filters && event.filters['sectorName']) {
    const sectorFilter = event.filters['sectorName'];
    if (sectorFilter.value && Array.isArray(sectorFilter.value)) {
      this.selectedSectors = sectorFilter.value;
    } else {
      this.selectedSectors = [];
    }
  } else {
    this.selectedSectors = [];
  }

  this.updateChart();
}

  onYearSelectionChange() {
    this.loadByYears(this.selectedYears);
  }

  onProspectSummaryDataTypeChange() { this.updateChart(); }
  onProspectSummaryGroupByChange() { this.updateChart(); }
  onCustomerScopeChange() { this.updateChart(); }
  onSortToggle() { this.isSorted = !this.isSorted; this.updateChart(); }

  onCustomerCountChange() {
    if (this.selectedCustomerCount < 1) this.selectedCustomerCount = 1;
    const max = this.prospectSummary.length;
    if (this.selectedCustomerCount > max) this.selectedCustomerCount = max;
    this.updateChart();
  }

  // Export Methods
  exportProspectSummaryExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredProspectSummary.map(summary => ({
        'Customer Name': summary.customerName,
        'Status': summary.status,
        'Prospect Count': summary.prospectCount,
        'Total Activities': summary.totalActivities,
        'Total Service Value': summary.totalServiceValue
      })));
      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(data, `ProspectSummary_${new Date().getTime()}.xlsx`);
    });
  }

  exportProspectSummaryPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Prospect Count', dataKey: 'prospectCount' },
      { header: 'Total Activities', dataKey: 'totalActivities' },
      { header: 'Total Service Value', dataKey: 'totalServiceValue' }
    ];
    const rows = this.filteredProspectSummary.map((row, index) => ({
      index: index + 1,
      customerName: row.customerName,
      status: row.status,
      prospectCount: row.prospectCount,
      totalActivities: row.totalActivities,
      totalServiceValue: row.totalServiceValue
    }));
    autoTable(doc, {
      columns,
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 20 }
    });
    doc.save('ProspectSummary.pdf');
  }

  exportProspectSummaryChartAsImage() {
    this.prepareChartForExport();
    const canvas = this.prospectSummaryChart.chart.canvas;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/png');
    ctx.restore();
    const link = document.createElement('a');
    link.href = base64Image;
    link.download = 'prospect-summary-chart.png';
    link.click();
    setTimeout(() => this.resetChartAfterExport(), 100);
  }

  exportProspectSummaryChartAsPDF() {
    this.prepareChartForExport();
    const canvas = this.prospectSummaryChart.chart.canvas;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/png');
    ctx.restore();
    const imgWidth = 280;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;
    const pdf = new jsPDF('landscape');
    pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
    pdf.save('prospect-summary-chart.pdf');
    setTimeout(() => this.resetChartAfterExport(), 100);
  }

  private prepareChartForExport() {
    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.options.plugins.legend.display = true;
      this.prospectSummaryChart.chart.options.plugins.legend.position = 'right';
      this.hiddenDatasets.forEach((hidden, i) => {
        const meta = this.prospectSummaryChart.chart.getDatasetMeta(i);
        meta.hidden = hidden;
      });
      this.prospectSummaryChart.chart.update();
    }
  }

  private resetChartAfterExport() {
    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.options.plugins.legend.display = false;
      this.prospectSummaryChart.chart.update();
    }
  }

  toggleDataset(index: number) {
    if (!this.prospectSummaryChart?.chart) return;
    this.hiddenDatasets[index] = !this.hiddenDatasets[index];
    const meta = this.prospectSummaryChart.chart.getDatasetMeta(index);
    meta.hidden = this.hiddenDatasets[index];
    this.prospectSummaryChart.chart.update();
  }

  isDatasetHidden(index: number): boolean {
    return this.hiddenDatasets[index] || false;
  }

  customLabelsPlugin = {
    id: 'customLabels',
    afterDraw: (chart: any) => {
      if (!chart?.ctx || !chart.scales?.y) return;
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      ctx.save();
      ctx.font = 'Bold 12px Helvetica';
      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const datasets = chart.data.datasets;
      if (!datasets?.length) { ctx.restore(); return; }
      chart.data.labels.forEach((_: any, index: number) => {
        let total = 0;
        datasets.forEach((dataset: any) => total += dataset.data[index] || 0);
        if (total > 0) {
          const lastMeta = chart.getDatasetMeta(datasets.length - 1);
          const bar = lastMeta.data[index];
          if (bar?.x !== undefined && yAxis.getPixelForValue) {
            const x = bar.x;
            const y = yAxis.getPixelForValue(total);
            let displayText = total.toString();
            if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
              displayText = `$${total.toLocaleString()}`;
            }
            ctx.fillText(displayText, x, y - 5);
          }
        }
      });
      ctx.restore();
    }
  };
}